import './integration.scss'

import { API, Card, CheckBox, Help, Input, Submit, ViewHeader, useGetAll } from '@enkla-gui'
import React, { useCallback, useEffect, useState } from 'react'
import { integrationhelper, notify } from '@/_helpers'

import { useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'

export default function IntegrationDetail() {

  const { id } = useParams()
  const [integration, setIntegration] = useState(null)
  const [saving, setSaving] = useState(false)
  const { data: companies, loading: companiesLoading } = useGetAll({ endpoint: 'api/company' })
  const { register, handleSubmit, reset, watch, getValues, setValue } = useForm()
  const [error, setError] = useState(null)


  const fetchIntegration = useCallback(() => {
    API.get('api/integration').then(integrations => {
      const found = integrations.find(i => i.id === id)
      if (found) {
        setIntegration(found)
        reset(found)
      }
    })
  }, [id, reset])

  useEffect(() => {
    fetchIntegration()
  }, [fetchIntegration])

  const getIntegrationIcon = (name) => {
    switch (name) {
      case 'FORTNOX': return 'images/integrations/fortnox.svg'
      case 'VISMA': return 'images/integrations/visma.svg'
      case 'BOKIO': return 'images/integrations/bokio.png'
      case 'FASDOK': return 'images/integrations/fastdok.svg'
      default: return null
    }
  }

  const getIntegrationColor = (name) => {
    switch (name) {
      case 'FORTNOX': return '#2B5F35'
      case 'VISMA': return '#E13A3E'
      case 'BOKIO': return '#0E9F8F'
      case 'FASDOK': return '#1677FF'
      default: return '#6c757d'
    }
  }

  const getConnectedCompanies = () => {
    if (!integration || !companies) return []
    return companies.filter(c => c.invoicingconfig?.integration_id === integration.id)
  }

  const getUnconnectedCompanies = () => {
    if (!integration || !companies) return []
    return companies.filter(c => c.invoicingconfig?.integration_id !== integration.id)
  }

  const isInvoicingIntegration = integration?.name === 'FORTNOX' || integration?.name === 'VISMA' || integration?.name === 'BOKIO'

  const update = data => {
    if (integration?.account_mappings == null) {
      delete data.account_mappings
    }
    setSaving(true)
    API.patch(`api/integration`, data)
      .then(res => {
        notify.toast('Integration uppdaterad')
        setSaving(false)
        fetchIntegration()
      })
      .catch(([err, res]) => {
        notify.toast(res, 'error')
        setError({
          message: res.message,
          data: res.data
        })
        setSaving(false)
      })
  }

  const remove = async () => {
    if (!(await notify.confirm(`Vill du ta bort kopplingen för ${integrationhelper.name(integration.name)}? Alla hyresavier som är kopplade med integrationen kommer att bli konverterade till manuella hyresavier.`))) return
    API.delete(`api/integration/${integration.id}`)
      .then(res => {
        notify.toast('Integration bortkopplad')
        window.location.hash = '/integration/overview'
      })
      .catch(([err, res]) => {
        notify.toast(res, 'error')
      })
  }

  const reconnect = () => {
    if (integration.name === 'FORTNOX') {
      API.get('api/fortnox/login').then(res => (window.location.href = res))
    } else if (integration.name === 'VISMA') {
      API.get('api/visma/login').then(res => (window.location.href = res))
    } else if (integration.name === 'BOKIO') {
      API.get('api/bokio/login').then(res => (window.location.href = res))
    }
  }

  const handleFakturaserviceToggled = async (isChecked, nextValue) => {
    if (!nextValue) return true
    return await notify.confirm("Jag är medveten om att fakturaservice endast fungerar om jag lagt till tjänsten i Fortnox")
  }

  if (!integration || companiesLoading) {
    return (
      <>
        <ViewHeader
          icon='fa-puzzle-piece'
          title='Integrationer'
          subtitle='Laddar...'
          return={{ link: '#!/integration/overview', title: 'Integrationer' }}
        />
        <Card loading={true} title='Integration' icon='fas fa-puzzle-piece' />
      </>
    )
  }

  return (
    <>
      <ViewHeader
        icon='fa-puzzle-piece'
        title='Integrationer'
        subtitle={integration.description}
        return={{ link: '#!/integration/overview', title: 'Integrationer' }}
      />

      <div className="integration-detail-page">
        <div className="integration-detail-page__header">
          <img
            src={getIntegrationIcon(integration.name)}
            alt={integration.name}
            className="integration-detail-page__logo"
          />
          <div className="integration-detail-page__info">
            <h2>{integration.description}</h2>
            <div className="integration-detail-page__meta">
              <span
                className="integration-detail-page__badge"
                style={{ background: getIntegrationColor(integration.name) }}
              >
                {integrationhelper.name(integration.name)}
              </span>
              <span className="integration-detail-page__date">
                <i className="fas fa-calendar-alt" />
                Kopplad {new Date(integration.created_at * 1000).toLocaleDateString('sv-SE')}
              </span>
            </div>
          </div>
          <div className="integration-detail-page__actions">
            <button className="btn btn-light" onClick={remove}>
              <i className="fas fa-unlink mr-5" /> Koppla bort
            </button>
          </div>
        </div>

        {integration.token_invalid ? (
          <Help type='danger' icon='fas fa-exclamation-triangle' header={false}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
              <div>
                <strong>Anslutningen är inte längre giltig.</strong><br />
                Åtkomst har gått ut eller blivit ogiltig. Återanslut för att integrationen ska fungera igen.
              </div>
              <button className="btn btn-danger" onClick={reconnect} style={{ whiteSpace: 'nowrap' }}>
                <i className="fas fa-link" /> Återanslut
              </button>
            </div>
          </Help>
        ):''}

        <div className="row">
          <div className={isInvoicingIntegration ? 'col-lg-6' : 'col-12'}>
            <Card
              title='Inställningar'
              icon='fas fa-cog'
              buttons={{
                footer: [<Submit key="save" onSubmit={handleSubmit(update)} text='Spara' sending={saving} />]
              }}
            >
              <div className='row mb-3'>
                <Input
                  controls={{ register }}
                  name='description'
                  options={{ required: true }}
                  label='Beskrivning'
                  error={error}
                />
              </div>
    {/*
               {integration.name === 'FORTNOX' && (
              <div className='row mb-3'>
                <div className='form-group'>
                  <label>Fakturaservice</label>
                  <br />
                  <CheckBox
                    error={error}
                    controls={{ register, getValues, setValue, watch }}
                    name='fortnox_fakturaservice'
                    options={{ required: false }}
                    label='Aktivera fakturaservice'
                    preChangeHook={handleFakturaserviceToggled}
                  />
                </div>
                </div>
              )}
    */}
              {integration.account_mappings != null && (
                <div>
                  <label>Kontomappning</label>
                  <p className='text-muted small'>Tänk på att samtliga konton måste vara aktiva i företagets kontoplan</p>
                  <div className='row'>
                    <Input
                      controls={{ register }}
                      name='account_mappings.0'
                      options={{ required: true, valueAsNumber: true, min: 1000, max: 9999 }}
                      label='0% moms'
                      type='number'
                      error={error}
                      cols='col-6'
                    />
                    <Input
                      controls={{ register }}
                      name='account_mappings.6'
                      options={{ required: true, valueAsNumber: true, min: 1000, max: 9999 }}
                      label='6% moms'
                      type='number'
                      error={error}
                      cols='col-6'
                    />
                    <Input
                      controls={{ register }}
                      name='account_mappings.12'
                      options={{ required: true, valueAsNumber: true, min: 1000, max: 9999 }}
                      label='12% moms'
                      type='number'
                      error={error}
                      cols='col-6'
                    />
                    <Input
                      controls={{ register }}
                      name='account_mappings.25'
                      options={{ required: true, valueAsNumber: true, min: 1000, max: 9999 }}
                      label='25% moms'
                      type='number'
                      error={error}
                      cols='col-6'
                    />
                  </div>
                </div>
              )}
            </Card>
          </div>

          {isInvoicingIntegration && (
            <div className="col-lg-6">
              <Card title='Anslutna företag' icon='fas fa-building'>
                <div className="integration-companies">
                  <div className="integration-companies__section">
                    {getConnectedCompanies().length > 0 ? (
                      <div className="integration-companies__list">
                        {getConnectedCompanies().map(company => (
                          <div key={company.id} className="integration-companies__item integration-companies__item--connected">
                            <div className="integration-companies__company-info">
                              <i className="fas fa-building" />
                              <span>{company.name}</span>
                            </div>
                            <div className="integration-companies__company-actions">
                              <a
                                href={`#!/company/${company.id}/invoicingconfig`}
                                className="integration-companies__link"
                              >
                                <i className="fas fa-cog" />
                                Inställningar
                              </a>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="integration-companies__empty">
                        <i className="fas fa-building" />
                        Inga företag använder denna integration ännu
                      </div>
                    )}
                  </div>

                  {getUnconnectedCompanies().length > 0 && (
                    <div className="integration-companies__section">
                      <h4>
                        <i className="fas fa-plus-circle text-muted" />
                        Koppla företag
                      </h4>
                      <div className="integration-companies__list">
                        {getUnconnectedCompanies().map(company => (
                          <div key={company.id} className="integration-companies__item">
                            <div className="integration-companies__company-info">
                              <i className="fas fa-building" />
                              <span>{company.name}</span>
                            </div>
                            <div className="integration-companies__company-actions">
                              <a
                                href={`#!/company/${company.id}/invoicingconfig?integration=${integration.id}`}
                                className="integration-companies__link integration-companies__link--primary"
                              >
                                <i className="fas fa-puzzle-piece" />
                                Koppla
                              </a>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            </div>
          )}
        </div>
      </div>
    </>
  )
}
